import { Patient } from '../components/patients/PatientCard';

export interface TreatmentPlanItem {
  id?: number;
  item_type: 'medication' | 'therapy' | 'procedure' | 'lifestyle' | 'follow_up' | 'lab_test' | 'imaging';
  title: string;
  description?: string;
  instructions?: string;
  frequency?: string;
  duration?: string;
  start_date?: string;
  end_date?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped' | 'cancelled';
  completion_notes?: string;
  order_sequence: number;
  is_critical: boolean;
  is_due?: boolean;
  is_overdue?: boolean;
  days_until_due?: number;
}

export interface TreatmentPlan {
  id?: number;
  patient_id: number;
  patient?: Patient;
  doctor_id: number;
  doctor?: {
    id: number;
    name: string;
    specialty?: string;
  };
  appointment_id?: number;
  plan_name: string;
  diagnosis?: string;
  objectives?: string;
  start_date: string;
  end_date?: string;
  status: 'active' | 'completed' | 'paused' | 'cancelled';
  priority: 'low' | 'medium' | 'high';
  progress_notes?: string;
  outcome?: string;
  progress_percentage: number;
  completed_items_count: number;
  total_items_count: number;
  pending_items_count: number;
  in_progress_items_count: number;
  critical_items_count: number;
  overdue_items_count: number;
  duration_in_days?: number;
  days_remaining?: number;
  is_overdue: boolean;
  items: TreatmentPlanItem[];
  created_at?: string;
  updated_at?: string;
}

export interface CreateTreatmentPlanData {
  patient_id: number;
  appointment_id?: number;
  plan_name: string;
  diagnosis?: string;
  objectives?: string;
  start_date: string;
  end_date?: string;
  priority: 'low' | 'medium' | 'high';
  progress_notes?: string;
  items: Array<{
    item_type: 'medication' | 'therapy' | 'procedure' | 'lifestyle' | 'follow_up' | 'lab_test' | 'imaging';
    title: string;
    description?: string;
    instructions?: string;
    frequency?: string;
    duration?: string;
    start_date?: string;
    end_date?: string;
    is_critical: boolean;
    order_sequence: number;
  }>;
}

export interface LaravelPaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
  };
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
}

class TreatmentPlanService {
  private static BASE_URL = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api';

  private static async request(url: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('auth_token');
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(`${this.BASE_URL}${url}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // Get treatment plans with filtering
  static async getTreatmentPlans(params?: {
    patient_id?: number;
    doctor_id?: number;
    status?: string;
    priority?: string;
    date_from?: string;
    date_to?: string;
    overdue?: boolean;
    ending_soon_days?: number;
    search?: string;
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<LaravelPaginatedResponse<TreatmentPlan>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const url = `/treatment-plans${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await this.request(url);
  }

  // Get single treatment plan
  static async getTreatmentPlan(id: number): Promise<TreatmentPlan> {
    const result = await this.request(`/treatment-plans/${id}`);
    return result.data;
  }

  // Create new treatment plan
  static async createTreatmentPlan(data: CreateTreatmentPlanData): Promise<TreatmentPlan> {
    const result = await this.request('/treatment-plans', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return result.data;
  }

  // Update treatment plan
  static async updateTreatmentPlan(id: number, data: Partial<CreateTreatmentPlanData>): Promise<TreatmentPlan> {
    const result = await this.request(`/treatment-plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return result.data;
  }

  // Delete treatment plan
  static async deleteTreatmentPlan(id: number): Promise<void> {
    await this.request(`/treatment-plans/${id}`, {
      method: 'DELETE',
    });
  }

  // Complete treatment plan
  static async completeTreatmentPlan(id: number, data?: {
    outcome?: string;
    progress_notes?: string;
  }): Promise<TreatmentPlan> {
    const result = await this.request(`/treatment-plans/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
    return result.data;
  }

  // Pause treatment plan
  static async pauseTreatmentPlan(id: number): Promise<TreatmentPlan> {
    const result = await this.request(`/treatment-plans/${id}/pause`, {
      method: 'POST',
    });
    return result.data;
  }

  // Resume treatment plan
  static async resumeTreatmentPlan(id: number): Promise<TreatmentPlan> {
    const result = await this.request(`/treatment-plans/${id}/resume`, {
      method: 'POST',
    });
    return result.data;
  }

  // Cancel treatment plan
  static async cancelTreatmentPlan(id: number): Promise<TreatmentPlan> {
    const result = await this.request(`/treatment-plans/${id}/cancel`, {
      method: 'POST',
    });
    return result.data;
  }

  // Add item to treatment plan
  static async addItemToPlan(planId: number, itemData: {
    item_type: string;
    title: string;
    description?: string;
    instructions?: string;
    frequency?: string;
    duration?: string;
    start_date?: string;
    end_date?: string;
    is_critical: boolean;
    order_sequence?: number;
  }): Promise<TreatmentPlanItem> {
    const result = await this.request(`/treatment-plans/${planId}/items`, {
      method: 'POST',
      body: JSON.stringify(itemData),
    });
    return result.data;
  }

  // Update treatment plan item
  static async updatePlanItem(planId: number, itemId: number, data: Partial<TreatmentPlanItem>): Promise<TreatmentPlanItem> {
    const result = await this.request(`/treatment-plans/${planId}/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return result.data;
  }

  // Complete treatment plan item
  // Complete treatment plan item - Updated with better error handling
  static async completeItem(planId: number, itemId: number, notes?: string): Promise<TreatmentPlanItem> {
    try {
      const requestBody: any = {};
      if (notes) {
        requestBody.completion_notes = notes;
      }

      const result = await this.request(`/treatment-plans/${planId}/items/${itemId}/complete`, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
      return result.data;
    } catch (error) {
      console.error('Error in completeItem:', error);
      throw error;
    }
  }

  // Remove treatment plan item
  static async removeItem(planId: number, itemId: number): Promise<void> {
    await this.request(`/treatment-plans/${planId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  // Get patient's treatment plans
  static async getPatientTreatmentPlans(patientId: number): Promise<TreatmentPlan[]> {
    const result = await this.request(`/treatment-plans?patient_id=${patientId}`);
    return result.data;
  }

  // Get treatment plan statistics
  static async getStatistics(): Promise<{
    total_plans: number;
    active_plans: number;
    completed_plans: number;
    overdue_plans: number;
    ending_soon_plans: number;
    high_priority_plans: number;
  }> {
    const result = await this.request('/treatment-plans/statistics');
    return result.data;
  }

  // Get available templates
  static async getTemplates(): Promise<Array<{
    id: string;
    name: string;
    description: string;
    category: string;
    items: Array<{
      item_type: string;
      title: string;
      description: string;
      frequency?: string;
      duration?: string;
      is_critical: boolean;
    }>;
  }>> {
    // Mock templates for now - replace with actual API call when backend implements templates
    return [
      {
        id: 'post_surgery',
        name: 'Post-Surgery Recovery Plan',
        description: 'Standard recovery plan for post-surgical patients',
        category: 'Surgery',
        items: [
          {
            item_type: 'medication',
            title: 'Pain Management',
            description: 'Take prescribed pain medications as directed',
            frequency: 'As prescribed',
            duration: '1 week',
            is_critical: true
          },
          {
            item_type: 'procedure',
            title: 'Wound Care',
            description: 'Daily wound cleaning and dressing change',
            frequency: 'Daily',
            duration: '2 weeks',
            is_critical: true
          },
          {
            item_type: 'follow_up',
            title: 'Post-Op Check-up',
            description: 'Follow-up appointment with surgeon',
            is_critical: true
          }
        ]
      },
      {
        id: 'diabetes_management',
        name: 'Diabetes Management Plan',
        description: 'Comprehensive diabetes care and monitoring plan',
        category: 'Chronic Disease',
        items: [
          {
            item_type: 'medication',
            title: 'Blood Sugar Medication',
            description: 'Take diabetes medication as prescribed',
            frequency: 'Daily',
            is_critical: true
          },
          {
            item_type: 'lifestyle',
            title: 'Blood Sugar Monitoring',
            description: 'Check blood sugar levels regularly',
            frequency: 'Daily',
            is_critical: true
          },
          {
            item_type: 'lifestyle',
            title: 'Diet Plan',
            description: 'Follow diabetic diet plan',
            frequency: 'Daily',
            is_critical: false
          }
        ]
      },
      {
        id: 'hypertension_management',
        name: 'Hypertension Management Plan',
        description: 'Blood pressure control and lifestyle management',
        category: 'Chronic Disease',
        items: [
          {
            item_type: 'medication',
            title: 'Blood Pressure Medication',
            description: 'Take antihypertensive medication daily',
            frequency: 'Daily',
            is_critical: true
          },
          {
            item_type: 'lifestyle',
            title: 'Blood Pressure Monitoring',
            description: 'Monitor blood pressure at home',
            frequency: 'Weekly',
            is_critical: false
          },
          {
            item_type: 'lifestyle',
            title: 'Regular Exercise',
            description: '30 minutes of moderate exercise',
            frequency: '5 times per week',
            is_critical: false
          }
        ]
      }
    ];
  }

  // Helper methods
  static getStatusColor(status: string): string {
    switch (status) {
      case 'active': return '#10b981';
      case 'completed': return '#6b7280';
      case 'paused': return '#f59e0b';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  }

  static getStatusLabel(status: string): string {
    switch (status) {
      case 'active': return 'Active';
      case 'completed': return 'Completed';
      case 'paused': return 'Paused';
      case 'cancelled': return 'Cancelled';
      default: return 'Unknown';
    }
  }

  static getStatusIcon(status: string): string {
    switch (status) {
      case 'active': return '🔄';
      case 'completed': return '✅';
      case 'paused': return '⏸️';
      case 'cancelled': return '❌';
      default: return '📋';
    }
  }

  static getPriorityColor(priority: string): string {
    switch (priority) {
      case 'low': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'high': return '#ef4444';
      default: return '#6b7280';
    }
  }

  static getPriorityLabel(priority: string): string {
    switch (priority) {
      case 'low': return 'Low';
      case 'medium': return 'Medium';
      case 'high': return 'High';
      default: return 'Unknown';
    }
  }

  static getPriorityIcon(priority: string): string {
    switch (priority) {
      case 'low': return '🟢';
      case 'medium': return '🟡';
      case 'high': return '🔴';
      default: return '⚪';
    }
  }

  static getItemTypeColor(itemType: string): string {
    switch (itemType) {
      case 'medication': return '#3b82f6';
      case 'therapy': return '#10b981';
      case 'procedure': return '#8b5cf6';
      case 'lifestyle': return '#f59e0b';
      case 'follow_up': return '#06b6d4';
      case 'lab_test': return '#84cc16';
      case 'imaging': return '#ec4899';
      default: return '#6b7280';
    }
  }

  static getItemTypeLabel(itemType: string): string {
    switch (itemType) {
      case 'medication': return 'Medication';
      case 'therapy': return 'Therapy';
      case 'procedure': return 'Procedure';
      case 'lifestyle': return 'Lifestyle';
      case 'follow_up': return 'Follow-up';
      case 'lab_test': return 'Lab Test';
      case 'imaging': return 'Imaging';
      default: return 'Unknown';
    }
  }

  static getItemTypeIcon(itemType: string): string {
    switch (itemType) {
      case 'medication': return '💊';
      case 'therapy': return '🏥';
      case 'procedure': return '⚕️';
      case 'lifestyle': return '🏃';
      case 'follow_up': return '📅';
      case 'lab_test': return '🧪';
      case 'imaging': return '📷';
      default: return '📋';
    }
  }

  static getItemStatusColor(status: string): string {
    switch (status) {
      case 'pending': return '#6b7280';
      case 'in_progress': return '#3b82f6';
      case 'completed': return '#10b981';
      case 'skipped': return '#f59e0b';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  }

  static getItemStatusLabel(status: string): string {
    switch (status) {
      case 'pending': return 'Pending';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'skipped': return 'Skipped';
      case 'cancelled': return 'Cancelled';
      default: return 'Unknown';
    }
  }

  static getItemStatusIcon(status: string): string {
    switch (status) {
      case 'pending': return '⏳';
      case 'in_progress': return '🔄';
      case 'completed': return '✅';
      case 'skipped': return '⏭️';
      case 'cancelled': return '❌';
      default: return '📋';
    }
  }

  static formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  static formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  static calculateProgress(plan: TreatmentPlan): {
    percentage: number;
    completedItems: number;
    totalItems: number;
    nextDueItem?: TreatmentPlanItem;
  } {
    const totalItems = plan.items.length;
    const completedItems = plan.items.filter(item => item.status === 'completed').length;
    const percentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
    
    // Find next due item
    const nextDueItem = plan.items
      .filter(item => item.status === 'pending' && item.start_date)
      .sort((a, b) => new Date(a.start_date!).getTime() - new Date(b.start_date!).getTime())[0];

    return {
      percentage,
      completedItems,
      totalItems,
      nextDueItem
    };
  }
}

export default TreatmentPlanService;